/* eslint-disable @next/next/no-page-custom-font */
/* eslint-disable @next/next/no-img-element */
import '../styles/globals.scss';
import LocationMessage from '../shared-components/LocationFetching/location';
import type { AppProps } from 'next/app';

const Footer = dynamic(() => import('../layouts/Footer/footer'));

import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import navigationalSchema from '../utils/navigationSchema';
import organizationSchema, {
  WebsiteSchema
} from '../utils/organizationalSchema';
import { PaxProvider } from '../utils/context/checkoutContext';
import { AuthContextProvider } from '../utils/context/authContext';
import NavBar from '../layouts/Navbar/NavBar';
import { Roboto } from 'next/font/google';
import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AttractionCheckoutProvider } from '../utils/attractionCheckoutContext';
import { useMediaQuery } from '../hooks/mediaQuery';
import { SearchProvider } from '../utils/context/searchContext';
import LocationBanner from '../shared-components/LocationBanner';
import { LoadingSpinner } from '../shared-components/LoadingSpinner/LoadingSpinner';
const Notifications = dynamic(
  () => import('../components/BookingNotifications')
);
const AppoloProvider = dynamic(() => import('../utils/dynamicAppolo'), {
  ssr: true
});
declare global {
  interface Window {
    gtag?: any;
  }
}
const roboto = Roboto({
  subsets: ['latin'],
  weight: ['400', '500', '700', '300', '900']
});
export default function App({ Component, pageProps }: AppProps) {
  const [delayAnalyticsLoad, setAnalyticsLoad] = useState<boolean>(false);
  const router = useRouter();
  const { slug2 } = router.query;
  const isMobileScreen = useMediaQuery(639);

  const [isPageLoading, setIsPageLoading] = useState(false);

  // Check if the second slug is "things-to-do"
  const isThingsToDoPage = slug2?.includes('things-to-do');

  const routesWithoutNavBar = [
    '/terms-and-conditions',
    '/privacy-policy',
    '/goat-terms-and-conditions',
    '/goat-privacy-policy',
    '/checkout',
    '/my-bookings/[slug]',
    '/signin',
    '/signup',
    '/tours/tour-gallery',
    '/forget-password'
  ];

  const routesWithoutFooter = [
    '/signin',
    '/signup',
    '/forget-password',
    '/reset-password',
    '/goat-terms-and-conditions',
    '/goat-privacy-policy',
    '/terms-and-conditions',
    '/privacy-policy',
    '/checkout',
    '/tours/[slug]',
    '/things-to-do'
  ];

  // Check if the current route is excluded
  const isExcludedRoute = routesWithoutFooter.includes(router.route || '');

  // Conditional rendering logic
  const shouldShowFooter = !(
    (isThingsToDoPage &&
      router.route === '/[slug]/[slug2]' &&
      isMobileScreen) ||
    isExcludedRoute
  );
  const allowedRoutes = ['/', '/checkout', '/tours/[slug]'];

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (url.includes('/tours/') && !url.includes('/tour-gallery')) {
        setIsPageLoading(true);
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      if (url.includes('/tours/') && !url.includes('/tour-gallery')) {
        setIsPageLoading(false);
      }
    };

    const handleRouteChangeError = (err: Error, url: string) => {
      if (url.includes('/tours/') && !url.includes('/tour-gallery')) {
        setIsPageLoading(false);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router.events]);

  useEffect(() => {
    const analyticsTimeout = setTimeout(() => {
      setAnalyticsLoad(true);
    }, 10000);
    return () => clearTimeout(analyticsTimeout);
  }, []);
  // const [isHydrated, setIsHydrated] = useState(false);
  // useEffect(() => {
  //   if (typeof window.gtag !== 'undefined') {
  //     setAnalyticsLoad(true);
  //   }
  // }, []);
  // useEffect(() => {
  //   setIsHydrated(true);
  // }, []);
  // if (!isHydrated) {
  //   return null; // Render nothing on the initial render to ensure consistency
  // }
  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="og:type" content="website" />
        <meta property="og:site_name" content="See Sight Tours" />
        <meta
          name="google-site-verification"
          content="5SV-gMUYtvmmRozSFJH07XSg4yE9xGoJlKrdQHlTC8U"
        />
        <meta
          name="ahrefs-site-verification"
          content="431271b3f80040b0fd81903d441552d9a41c3267658827689e98a6712dd0e4c5"
        ></meta>

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(navigationalSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              url: 'https://www.seesight-tours.com/',
              name: 'See Sight Tours'
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(WebsiteSchema)
          }}
        />

        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-M75ZD2V8"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>
      </Head>

      <>
        {/* Production Scripts  */}
        {process.env.NEXT_PUBLIC_MODE === 'LIVE' && (
          <>
            {/*Clarity */}
            <Script id="microsoft-clarity" strategy="afterInteractive">
              {`
              (function(c,l,a,r,i,t,y){
                  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "ij2ib4cmwb");
            `}
            </Script>

            {/* Facebook Pixel: Load after the page is interactive */}
            {/* <Script id="facebook-pixel" strategy="afterInteractive">
              {`
              !function(f,b,e,v,n,t,s) {
                if(f.fbq)return; n=f.fbq=function(){n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments)};
                if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0';
                n.queue=[]; t=b.createElement(e); t.async=!0;
                t.src=v; s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)
              }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '466082886032975');
              fbq('track', 'PageView');
            `}
            </Script> */}
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                alt=""
                src="https://www.facebook.com/tr?id=466082886032975&ev=PageView&noscript=1"
              />
            </noscript>

            {/* GTM */}

            <Script id="google-tag-manager" strategy="beforeInteractive">
              {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-M75ZD2V8');  
              `}
            </Script>

            <Script
              async
              src="https://www.googletagmanager.com/gtag/js?id=G-2JP75NJVWC"
              id="gtg"
            ></Script>
            <Script id="load gtg">
              {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-2JP75NJVWC');
            `}
            </Script>
          </>
        )}

        {/* <Script
            id="batbing"
            defer
          >{`(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"27022747"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");`}</Script> */}
      </>

      <AppoloProvider>
        <main
          className={`${roboto.className} 
          }`}
        >
          {isPageLoading && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
              style={{ zIndex: 9999 }}
            >
              <LoadingSpinner className="w-12 h-12 border-white mr-2" />
            </div>
          )}

          {/* {delayAnalyticsLoad && <Notifications />} */}
          {/* {!hideLocationTrackerBanner && <LocationBanner />} */}
          <div className="min-h-screen scroll-m-96" id="website-navbar">
            <PaxProvider>
              <AttractionCheckoutProvider>
                <SearchProvider>
                  <AuthContextProvider>
                    <LocationBanner />
                    <LocationMessage />
                    {!routesWithoutNavBar.includes(router.route) && <NavBar />}
                    <Component {...pageProps} />
                  </AuthContextProvider>
                </SearchProvider>
              </AttractionCheckoutProvider>
              {shouldShowFooter && <Footer />}
            </PaxProvider>
          </div>

          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            limit={1}
          />
        </main>
      </AppoloProvider>
    </>
  );
}
